import { useState } from 'react';
import { Box, Typography, TextField, Button, RadioGroup, FormControlLabel, Radio, CircularProgress, Alert } from '@mui/material';
import { Search as SearchIcon, Add as AddIcon } from '@mui/icons-material';
import { usePatients } from '@/hooks/usePatients';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import { useDebounce } from 'use-debounce';
import { format } from 'date-fns';

/**
 * PatientSelectionPanel - Search and select existing patients
 */
export function PatientSelectionPanel() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebounce(searchQuery, 300);
  const selectedPatientId = useMeasurementWizardStore((s) => s.selectedPatientId);
  const setSelectedPatientId = useMeasurementWizardStore((s) => s.setSelectedPatientId);
  const setPatientPanelMode = useMeasurementWizardStore((s) => s.setPatientPanelMode);

  const { data: patients, isLoading, isError, refetch } = usePatients(debouncedSearch);

  const handleSelectPatient = (patientId: string) => {
    setSelectedPatientId(patientId);
    setPatientPanelMode('details');
  };

  const handleAddNew = () => {
    setPatientPanelMode('add-new');
  };

  const handleAnonymous = () => {
    setSelectedPatientId(null);
    setPatientPanelMode('anonymous');
  };

  const formatDate = (date: Date | null) => {
    if (!date) return '';
    return format(new Date(date), 'MMMM d, yyyy');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Select patient</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            onClick={handleAnonymous}
            sx={{ textTransform: 'none' }}
          >
            Anonymous Patient
          </Button>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ textTransform: 'none' }}
          >
            Add new
          </Button>
        </Box>
      </Box>

      <TextField
        fullWidth
        placeholder="Search"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
        }}
        sx={{ mb: 2 }}
      />

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }>
          Failed to load patients
        </Alert>
      )}

      {!isLoading && !isError && patients && (
        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          <RadioGroup 
            value={selectedPatientId || ''} 
            onChange={(e) => handleSelectPatient(e.target.value)}
          >
            {patients.map((patient) => (
              <FormControlLabel
                key={patient.id}
                value={patient.id}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">
                      {patient.fullName}
                    </Typography>
                    {patient.dateOfBirth && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(patient.dateOfBirth)}
                      </Typography>
                    )}
                  </Box>
                }
                sx={{ 
                  mb: 1,
                  border: '1px solid',
                  borderColor: selectedPatientId === patient.id ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  p: 1,
                  mr: 0,
                }}
              />
            ))}
          </RadioGroup>
        </Box>
      )}

      {!isLoading && !isError && patients && patients.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No patients found
        </Typography>
      )}
    </Box>
  );
}